import React, { useCallback, useMemo } from 'react'
import { useBulkCreateShiftsContextValue } from '../../context/BulkCreateShiftsProvider'
import {
  FieldErrors,
  FieldErrorsImpl,
  FormProvider,
  GlobalError,
  useForm,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ShiftsForm } from './ShiftsTable/ShiftsForm'
import { Footer } from '../Footer'
import { Box, Button, Flex, Modal } from '@workwhile/ui'
import {
  ShiftRowSchema,
  ShiftsTableSchema,
  shiftsTableSchema,
} from '../../bulkCreateShiftsSchema'
import styles from './Import.module.css'
import { BlockerFunction, useBlocker } from 'react-router'

export const Import = () => {
  const {
    state: {
      formState,
      checkLocationsState,
      createLocationsState,
      createShiftsError,
    },
    actions: { setFormState, checkLocations, replace },
  } = useBulkCreateShiftsContextValue()

  const errors = useMemo(() => {
    if (!formState.shifts) {
      return undefined
    }

    const shiftsErrors: FieldErrorsImpl<ShiftRowSchema>[] = []
    const rootErrors: Record<string, GlobalError> = {}

    formState.shifts.forEach((shift) => {
      const locationError =
        checkLocationsState.error?.locations?.[shift.rowId] ||
        createLocationsState.error?.locations?.[shift.rowId]
      const shiftError = createShiftsError?.shifts?.[shift.rowId]

      if (!locationError && !shiftError) {
        return
      }

      const fieldErrors: FieldErrorsImpl<ShiftRowSchema> = {}
      Object.entries(locationError ?? {}).forEach(([fieldName, messages]) => {
        fieldErrors[fieldName] = { message: messages.join(', ') }
      })

      Object.entries(shiftError ?? {}).forEach(([fieldName, messages]) => {
        fieldErrors[fieldName] = { message: messages.join(', ') }
      })

      // append general error messages to companyId field
      if (shiftError?.messages?.length) {
        let newErrorMessage = fieldErrors?.companyId?.message
        if (newErrorMessage) {
          newErrorMessage += `, ${shiftError?.messages?.join(', ')}`
        } else {
          newErrorMessage = shiftError?.messages?.join(', ')
        }
      }

      shiftsErrors.push(fieldErrors)

      // Shift-level errors from API
      if (shiftError?.messages?.length) {
        rootErrors[shift.rowId] = {
          message: shiftError?.messages?.join(', '),
        }
      }
    })

    return {
      shifts: shiftsErrors,
      root: rootErrors,
    } as FieldErrors<ShiftsTableSchema>
  }, [
    createLocationsState.error,
    checkLocationsState.error,
    createShiftsError,
    formState.shifts,
  ])

  const methods = useForm({
    resolver: zodResolver(shiftsTableSchema),
    defaultValues: formState,
    errors,
  })

  const {
    handleSubmit,
    formState: { errors: formStateErrors },
    getValues,
  } = methods

  const shouldBlockNavigation = useCallback<BlockerFunction>(() => {
    const shifts = getValues('shifts') ?? []
    return !!shifts.length
  }, [getValues])

  const blocker = useBlocker(shouldBlockNavigation)

  const handleNext = async (data: ShiftsTableSchema) => {
    setFormState(data)
    const result = await checkLocations(data)
    if (result) {
      replace('review')
    }
  }

  return (
    <FormProvider {...methods}>
      <Flex flexDirection="column" gap={3} height="100%" p={3}>
        <Flex
          flexDirection="column"
          gap={3}
          className={styles.container}
          height="100%"
        >
          <Box className={styles.content}>
            <ShiftsForm />
          </Box>
        </Flex>
        <Footer error={formStateErrors?.root?.message}>
          <Button
            variant="primary"
            onClick={handleSubmit((data) => {
              handleNext(data)
            })}
            disabled={checkLocationsState.isPending}
            loading={checkLocationsState.isPending}
          >
            {checkLocationsState.isPending
              ? 'Checking locations...'
              : 'Next: Review'}
          </Button>
        </Footer>
      </Flex>
      {blocker.state === 'blocked' && (
        <Modal
          open
          title="Unsaved changes"
          description="You have unsaved changes. Are you sure you want to leave?"
          okText="Leave"
          onOk={() => blocker.proceed()}
          showCancel
          cancelText="Cancel"
          onClose={() => blocker.reset()}
        />
      )}
    </FormProvider>
  )
}
